import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, Loader2, X, Check, Ban, Share2, Download, Sparkles, Pencil, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, mediaUrl } from "../../lib/api";
import SelectField from "../../components/os/SelectField";
import DatePicker from "../../components/os/DatePicker";
import { MODULES } from "./modulesConfig";
import ModuleResponses from "./ModuleResponses";
import ModuleShare from "./ModuleShare";
import ModuleDashboard from "./ModuleDashboard";
import ModuleDocs from "./ModuleDocs";
import ModuleAnalyze from "./ModuleAnalyze";
import ModuleMeeting from "./ModuleMeeting";
import ModuleAI from "./ModuleAI";
import ModuleVault from "./ModuleVault";
import ModuleNotify from "./ModuleNotify";
import ModuleProbe from "./ModuleProbe";
import ModuleComputed from "./ModuleComputed";
import ModuleSearch from "./ModuleSearch";
import ModuleLinks from "./ModuleLinks";
import CrossSuiteTool from "../../components/os/CrossSuiteTool";
import RetailPOS from "../../components/os/RetailPOS";
import RetailDemand from "../../components/os/RetailDemand";
import RetailPromoSimulator from "../../components/os/RetailPromoSimulator";
import RetailCedisReception from "../../components/os/RetailCedisReception";
import FieldDispatch from "../../components/os/FieldDispatch";
import FieldTicketOps from "../../components/os/FieldTicketOps";
import WorkshopWorkOrder from "../../components/os/WorkshopWorkOrder";
import WorkshopSupplierPortal from "../../components/os/WorkshopSupplierPortal";
import RetailDashboard from "../../components/os/RetailDashboard";
import FieldSLA from "../../components/os/FieldSLA";
import FieldMyDay from "../../components/os/FieldMyDay";
import WorkshopPreventive from "../../components/os/WorkshopPreventive";
import RetailPODistribution from "../../components/os/RetailPODistribution";
import RetailPOItems from "../../components/os/RetailPOItems";
import RetailStockByStore from "../../components/os/RetailStockByStore";
import RetailAutoDistribution from "../../components/os/RetailAutoDistribution";
import RetailPromoAI from "../../components/os/RetailPromoAI";
import FieldRecordPDF from "../../components/os/FieldRecordPDF";
import ModuleVersions from "./ModuleVersions";
import ModuleQuote from "./ModuleQuote";
import ModulePipeline from "./ModulePipeline";
import Customer360 from "./Customer360";
import CustomerTimeline from "./CustomerTimeline";
import ModuleLeadScore from "./ModuleLeadScore";
import ModuleToProject from "./ModuleToProject";
import ModuleLog from "./ModuleLog";
import ModuleInsights from "./ModuleInsights";
import ProjectTimeline from "./ProjectTimeline";
import ModuleLanding from "./ModuleLanding";
import ModuleEmail from "./ModuleEmail";
import ModuleCampaigns from "./ModuleCampaigns";
import ModuleAutomation from "./ModuleAutomation";
import { AudiencePicker, ReadTracker, ReceivedInbox } from "./ModuleAudience";
import ModuleComments from "./ModuleComments";
import ModuleAIImage from "./ModuleAIImage";
import ModuleStages from "./ModuleStages";
import ModuleEventFinance from "./ModuleEventFinance";
import ModuleAssetBank from "./ModuleAssetBank";
import ModuleLegalIntel from "./ModuleLegalIntel";
import ModuleLegalDraft from "./ModuleLegalDraft";
import { EmployeePicker } from "../../components/os/EmployeePicker";
import { useAuth } from "../../context/AuthContext";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm";
const CURR_LOCALE = { MXN: "es-MX", USD: "en-US", ARS: "es-AR", EUR: "es-ES", COP: "es-CO", CLP: "es-CL", PEN: "es-PE", BRL: "pt-BR", GTQ: "es-GT", CRC: "es-CR", USDC: "en-US" };
const money = (v, code = "MXN") => { try { return Number(v).toLocaleString(CURR_LOCALE[code] || "es-MX", { style: "currency", currency: code, maximumFractionDigits: 0 }); } catch { return `$${Number(v).toLocaleString("es-MX")}`; } };
function fmt(field, v, row) {
  if (field.type === "employee") return (row && row[`${field.key}_name`]) || (v ? "—" : "—");
  if (v == null || v === "") return "—";
  if (field.type === "currency") return money(v, (row && row.moneda) || "MXN");
  if (field.type === "percent") return `${v}%`;
  if (field.type === "image") return v ? "🖼️" : "—";
  if (field.type === "select") return (field.options || []).find((o) => String(o.value) === String(v))?.label || v;
  return String(v);
}
function ImageField({ field, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const up = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(data.url); }
    catch { toast.error("Error al subir la imagen"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={mediaUrl(value)} alt="" className="w-16 h-16 rounded-full object-cover border border-navy/10" /> : <div className="w-16 h-16 rounded-full bg-navy/5 flex items-center justify-center text-navy/30"><ImageIcon className="w-5 h-5" /></div>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={up} data-testid={`mod-f-${field.key}`} />
      <button type="button" onClick={() => ref.current?.click()} className="btn btn-outline text-sm">{busy ? "Subiendo…" : value ? "Cambiar foto" : "Subir foto"}</button>
    </div>
  );
}
function RefField({ field, value, onChange }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    api.get(`/mod/${field.options}/items`).then((r) => {
      setOpts((r.data || []).map((x) => x.nombre || x.titulo || x.asunto || x.hito || x.metrica).filter(Boolean));
    }).catch(() => {});
  }, [field.options]);
  return <SelectField testid={`mod-f-${field.key}`} value={value ? String(value) : undefined} placeholder={`Selecciona ${field.label.toLowerCase()}`} options={opts.map((o) => ({ value: o, label: o }))} onChange={onChange} />;
}

function Field({ field, value, onChange }) {
  if (field.type === "textarea") return <textarea className={input} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`mod-f-${field.key}`} />;
  if (field.type === "select") return <SelectField testid={`mod-f-${field.key}`} value={value ? String(value) : undefined} placeholder={`Selecciona ${field.label.toLowerCase()}`} options={field.options || []} onChange={onChange} />;
  if (field.type === "ref") return <RefField field={field} value={value} onChange={onChange} />;
  if (field.type === "image") return <ImageField field={field} value={value} onChange={onChange} />;
  if (field.type === "date") return <DatePicker testid={`mod-f-${field.key}`} value={value || ""} onChange={onChange} placeholder={field.label} />;
  const t = ["number", "currency", "percent"].includes(field.type) ? "number" : "text";
  return <input type={t} step="any" className={input} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`mod-f-${field.key}`} />;
}

// Campo de colaborador estándar (G-001) / usuario autenticado automático (G-002).
// - self:true  → se autocompleta con el usuario en sesión y NO es editable.
// - normal     → selección exclusiva desde el directorio (EmployeePicker).
function EmployeeField({ field, value, name, onPick, me }) {
  useEffect(() => {
    if (field.self && me?.id && value !== me.id) onPick({ id: me.id, name: me.name });
    // eslint-disable-next-line
  }, [field.self, me?.id]);
  if (field.self) {
    return (
      <div data-testid={`mod-self-${field.key}`}
        className="w-full flex items-center gap-2 rounded-lg border border-navy/10 bg-navy/[0.03] px-3 py-2 text-sm text-navy/70">
        <span className="w-5 h-5 rounded-full bg-gradient-violet text-white text-[10px] flex items-center justify-center">
          {(me?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}
        </span>
        {me?.name || "—"} <span className="text-navy/35 text-xs">· tú</span>
      </div>
    );
  }
  return (
    <EmployeePicker testid={`mod-emp-${field.key}`} value={value || null}
      scope={field.scope || ""} onChange={(emp) => onPick(emp)} />
  );
}


export default function ModulePage() {
  const { key } = useParams();
  const { user: me } = useAuth();
  const [sp, setSp] = useSearchParams();
  const cfg = MODULES[key];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [share, setShare] = useState(false);
  const [form, setForm] = useState({});
  const [inbox, setInbox] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [fMonth, setFMonth] = useState("all");
  const [fYear, setFYear] = useState("all");

  const load = () => { setLoading(true); api.get(`/mod/${key}/items`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  const refreshDetail = async () => {
    const r = await api.get(`/mod/${key}/items`); setItems(r.data);
    setDetail((d) => (d ? r.data.find((x) => x.id === d.id) || d : d));
  };
  useEffect(() => { setEditing(false); }, [detail?.id]);
  useEffect(() => { setForm({}); setDetail(null); setShare(false); if (cfg) load(); /* eslint-disable-next-line */ }, [key]);
  useEffect(() => { if (cfg && sp.get("new") === "1") { setOpen(true); setSp({}, { replace: true }); } /* eslint-disable-next-line */ }, [sp, key]);
  useEffect(() => {
    const oid = sp.get("open");
    if (cfg && oid && items.length) {
      const it = items.find((x) => x.id === oid);
      if (it) { setDetail(it); setSp({}, { replace: true }); }
    } /* eslint-disable-next-line */
  }, [sp, key, items]);

  if (!cfg) return <OSLayout title="Módulo"><div className="card-surface p-8 text-center text-navy/40">Módulo no configurado.</div></OSLayout>;
  // Lote 3/4 — Consolidación: las apps duplicadas se absorben en el módulo canónico.
  const REDIRECTS = {
    attendance: "/os/workforce?tab=attendance", shifts: "/os/workforce?tab=shifts", time_tracking: "/os/workforce?tab=timesheets",
    goals: "/os/gestion-talento?tab=desempeno", performance_reviews: "/os/gestion-talento?tab=desempeno",
    feedback: "/os/gestion-talento?tab=desempeno", continuous_performance: "/os/gestion-talento?tab=desempeno",
    nine_box: "/os/gestion-talento?tab=desempeno", talent_review: "/os/gestion-talento?tab=desempeno",
    succession_planning: "/os/gestion-talento?tab=desempeno",
    competencies: "/os/gestion-talento?tab=competencias", 
    learning_courses: "/os/gestion-talento?tab=aprendizaje",
  };
  if (REDIRECTS[key]) return <Navigate to={REDIRECTS[key]} replace />;

  const first = cfg.fields[0];
  const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const dateFieldKey = cfg.monthField || (cfg.fields.find((x) => x.type === "date") || {}).key || "created_at";
  const rowDate = (it) => { const v = it[dateFieldKey] || it.created_at; const d = v ? new Date(v) : null; return d && !isNaN(d) ? d : null; };
  const yearsAvail = cfg.monthFilter ? Array.from(new Set(items.map((it) => rowDate(it) && rowDate(it).getFullYear()).filter(Boolean))).sort((a, b) => b - a) : [];
  const filteredItems = cfg.monthFilter ? items.filter((it) => {
    const d = rowDate(it); if (!d) return fMonth === "all" && fYear === "all";
    if (fYear !== "all" && d.getFullYear() !== Number(fYear)) return false;
    if (fMonth !== "all" && d.getMonth() !== Number(fMonth)) return false;
    return true;
  }) : items;
  const submit = async () => {
    if (!cfg.autoFirst && !String(form[first.key] || "").trim()) return toast.error(`${first.label} es obligatorio`);
    try { await api.post(`/mod/${key}/items`, form); setOpen(false); setForm({}); load(); toast.success(`Se creó: ${cfg.record}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const setStatus = async (it, estado) => { try { await api.patch(`/mod/${key}/items/${it.id}`, { estado }); await refreshDetail(); } catch { toast.error("Error"); } };
  const startEdit = () => { setEditForm({ ...detail }); setEditing(true); };
  const saveEdit = async () => {
    if (!String(editForm[first.key] || "").trim()) return toast.error(`${first.label} es obligatorio`);
    const payload = {}; cfg.fields.forEach((fd) => { payload[fd.key] = editForm[fd.key]; if (fd.type === "employee") payload[`${fd.key}_name`] = editForm[`${fd.key}_name`]; });
    try { await api.patch(`/mod/${key}/items/${detail.id}`, payload); await refreshDetail(); setEditing(false); toast.success("Cambios guardados"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { try { await api.delete(`/mod/${key}/items/${id}`); setDetail(null); load(); } catch { /* */ } };

  return (
    <OSLayout title={cfg.title}>
      <div className="space-y-6" data-testid="module-page">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="overline text-navy/40 mb-2">OMNIA Enterprise OS™</div>
            <h2 className="font-editorial text-4xl tracking-tight" data-testid="module-title">{cfg.title}</h2>
            <p className="mt-2 text-navy/50 max-w-2xl">{cfg.desc}</p>
          </div>
          {cfg.autolead && !cfg.viewer && (
            <button onClick={async () => { try { const { data } = await api.post(`/mod/${key}/autogenerate`); toast.success(`${data.created} lead(s) auto-generados`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } }} className="btn btn-outline shrink-0" data-testid="module-autolead"><Sparkles className="w-4 h-4" /> Auto-generar leads</button>
          )}
          {!cfg.viewer && !cfg.computedOnly && !cfg.assetbank && <button onClick={() => { setForm({ ...(cfg.workflow ? { estado: "pendiente" } : {}), ...(cfg.audience ? { audiencia: { scope: "empresa", areas: [], user_ids: [] } } : {}) }); setOpen(true); }} className="btn btn-primary shrink-0" data-testid="module-new"><Plus className="w-4 h-4" /> Nuevo: {cfg.record}</button>}
        </div>

        {cfg.computed && <ModuleComputed moduleKey={key} />}

        {cfg.audience && (
          <div className="flex gap-2" data-testid="audience-tabs">
            <button onClick={() => setInbox(false)} data-testid="tab-mine" className={`text-sm px-4 py-2 rounded-lg transition-colors ${!inbox ? "bg-gradient-violet text-white" : "bg-navy/[0.04] text-navy/60"}`}>Publicados por mí</button>
            <button onClick={() => setInbox(true)} data-testid="tab-received" className={`text-sm px-4 py-2 rounded-lg transition-colors ${inbox ? "bg-gradient-violet text-white" : "bg-navy/[0.04] text-navy/60"}`}>Recibidos</button>
          </div>
        )}
        {cfg.audience && inbox && <ReceivedInbox moduleKey={key} cfg={cfg} onOpen={setDetail} />}
        {cfg.search && <ModuleSearch />}
        {cfg.pipeline && <ModulePipeline />}
        {cfg.viewer === "customer360" && <Customer360 />}
        {cfg.viewer === "timeline" && <CustomerTimeline />}
        {cfg.viewer === "projtimeline" && <ProjectTimeline />}
        {cfg.viewer === "pos" && <RetailPOS />}
        {cfg.viewer === "demand" && <RetailDemand />}
        {cfg.viewer === "dispatch" && <FieldDispatch />}
        {cfg.viewer === "supplier" && <WorkshopSupplierPortal />}
        {cfg.viewer === "retaildash" && <RetailDashboard />}
        {cfg.viewer === "sla" && <FieldSLA />}
        {cfg.viewer === "myday" && <FieldMyDay />}
        {cfg.viewer === "preventive" && <WorkshopPreventive />}
        {cfg.viewer === "stockmap" && <RetailStockByStore />}
        {cfg.viewer === "autodist" && <RetailAutoDistribution />}
        {cfg.viewer === "promoai" && <RetailPromoAI />}

        {cfg.insights && !cfg.board && !cfg.viewer && <ModuleInsights moduleKey={key} />}

        {cfg.board && (loading
          ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : items.length === 0
            ? <div className="card-surface py-14 text-center text-navy/40 text-sm" data-testid="module-empty">Aún no hay registros. Crea el primero.</div>
            : <ModuleCampaigns items={items} onOpen={setDetail} />)}

        {cfg.assetbank && (loading
          ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : <ModuleAssetBank moduleKey={key} items={items} onOpen={setDetail} onRefresh={load} />)}

        {cfg.viewer || cfg.computedOnly || cfg.board || cfg.assetbank || (cfg.audience && inbox) ? null : loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          items.length === 0 ? <div className="card-surface py-14 text-center text-navy/40 text-sm" data-testid="module-empty">Aún no hay registros. Crea el primero.</div> :
          <>
          {cfg.monthFilter && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="month-filter">
              <span className="text-xs text-navy/40">Filtrar por:</span>
              <select value={fYear} onChange={(e) => setFYear(e.target.value)} className="text-sm border border-navy/15 rounded-lg px-2.5 py-1.5 bg-white" data-testid="filter-year">
                <option value="all">Todos los años</option>
                {yearsAvail.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <select value={fMonth} onChange={(e) => setFMonth(e.target.value)} className="text-sm border border-navy/15 rounded-lg px-2.5 py-1.5 bg-white" data-testid="filter-month">
                <option value="all">Todos los meses</option>
                {MONTHS_ES.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              {(fYear !== "all" || fMonth !== "all") && <button onClick={() => { setFYear("all"); setFMonth("all"); }} className="text-xs text-violet hover:underline" data-testid="filter-clear">Limpiar</button>}
              <span className="text-xs text-navy/40 ml-auto">{filteredItems.length} registro(s)</span>
            </div>
          )}
          {cfg.dashboard && <ModuleDashboard cfg={cfg} items={filteredItems} />}
          <div className="card-surface overflow-x-auto" data-testid="module-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-navy/10 text-navy/50">{cfg.columns.map((c) => { const fd = cfg.fields.find((x) => x.key === c); return <th key={c} className="text-left font-medium px-4 py-3 whitespace-nowrap">{fd?.label || c}</th>; })}<th /></tr></thead>
              <tbody>
                {filteredItems.map((it) => (
                  <tr key={it.id} onClick={() => setDetail(it)} className="border-b border-navy/5 last:border-0 hover:bg-navy/[0.03] cursor-pointer" data-testid={`module-row-${it.id}`}>
                    {cfg.columns.map((c) => { const fd = cfg.fields.find((x) => x.key === c); return <td key={c} className="px-4 py-3 whitespace-nowrap">{fd ? fmt(fd, it[c], it) : it[c]}</td>; })}
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {cfg.workflow && it.estado === "pendiente" && (
                        <span className="inline-flex gap-1">
                          <button onClick={() => setStatus(it, "aprobado")} className="text-emerald-600 hover:text-emerald-700" title="Aprobar" data-testid={`module-approve-${it.id}`}><Check className="w-4 h-4" /></button>
                          <button onClick={() => setStatus(it, "rechazado")} className="text-destructive/70 hover:text-destructive" title="Rechazar" data-testid={`module-reject-${it.id}`}><Ban className="w-4 h-4" /></button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>}
      </div>

      {open && <Modal title={`Nuevo: ${cfg.record}`} onClose={() => setOpen(false)} testid="module-dialog">
        <div className="space-y-3">
          {cfg.fields.map((fd) => (
            <div key={fd.key}><label className="text-xs text-navy/50 mb-1 block">{fd.label}</label>
              {fd.type === "employee"
                ? <EmployeeField field={fd} value={form[fd.key]} name={form[`${fd.key}_name`]} me={me}
                    onPick={(emp) => setForm({ ...form, [fd.key]: emp?.id || "", [`${fd.key}_name`]: emp?.name || "" })} />
                : <Field field={fd} value={form[fd.key]} onChange={(v) => setForm({ ...form, [fd.key]: v })} />}
            </div>
          ))}
          {cfg.audience && <AudiencePicker value={form.audiencia} onChange={(a) => setForm({ ...form, audiencia: a })} />}
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={submit} className="btn btn-primary" data-testid="module-submit">Crear</button></div>
      </Modal>}

      {detail && <Modal title={detail[first.key] || cfg.record} onClose={() => { setDetail(null); setEditing(false); }} testid="module-detail" wide={cfg.wide} xwide={cfg.xwide}>
        {!cfg.viewer && !cfg.computedOnly && (
          <div className="flex justify-end -mt-2 mb-2">
            {editing
              ? <div className="flex gap-2">
                  <button onClick={() => setEditing(false)} className="btn btn-ghost text-sm" data-testid="module-edit-cancel">Cancelar</button>
                  <button onClick={saveEdit} className="btn btn-primary text-sm" data-testid="module-edit-save"><Check className="w-4 h-4" /> Guardar</button>
                </div>
              : <button onClick={startEdit} className="btn btn-outline text-sm" data-testid="module-edit"><Pencil className="w-4 h-4" /> Editar</button>}
          </div>
        )}
        {editing
          ? <div className="space-y-3" data-testid="module-edit-form">
              {cfg.fields.map((fd) => (
                <div key={fd.key}><label className="text-xs text-navy/50 mb-1 block">{fd.label}</label>
                  {fd.type === "employee"
                    ? <EmployeeField field={fd} value={editForm[fd.key]} name={editForm[`${fd.key}_name`]} me={me}
                        onPick={(emp) => setEditForm({ ...editForm, [fd.key]: emp?.id || "", [`${fd.key}_name`]: emp?.name || "" })} />
                    : <Field field={fd} value={editForm[fd.key]} onChange={(v) => setEditForm({ ...editForm, [fd.key]: v })} />}
                </div>
              ))}
            </div>
          : <div className="grid gap-y-2">
              {cfg.fields.map((fd) => (
                fd.type === "image"
                  ? <div key={fd.key} className="flex justify-between items-center gap-3 border-b border-navy/5 py-1.5 text-sm"><span className="text-navy/45">{fd.label}</span>{detail[fd.key] ? <img src={mediaUrl(detail[fd.key])} alt="" className="w-14 h-14 rounded-full object-cover border border-navy/10" /> : <span className="text-navy/40">—</span>}</div>
                  : <div key={fd.key} className="flex justify-between gap-3 border-b border-navy/5 py-1.5 text-sm"><span className="text-navy/45">{fd.label}</span><span className="text-navy/80 text-right max-w-[60%]">{fmt(fd, detail[fd.key], detail)}</span></div>
              ))}
            </div>}
        {cfg.stages && <ModuleStages moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.legalai && <ModuleLegalIntel moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.legaldraft && <ModuleLegalDraft moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.finance && <ModuleEventFinance moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.aiimage && <ModuleAIImage moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.responder && <ModuleResponses moduleKey={key} item={detail} kind={cfg.responder} />}
        {(cfg.files || cfg.ocr || cfg.template) && <ModuleDocs moduleKey={key} item={detail} cfg={cfg} onRefresh={refreshDetail} />}
        {cfg.analyze === "decision" && <ModuleAnalyze moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.meeting && <ModuleMeeting moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.ai && <ModuleAI moduleKey={key} item={detail} label={cfg.ai} onRefresh={refreshDetail} />}
        {cfg.vault && <ModuleVault />}
        {cfg.notify && <ModuleNotify moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.probe && <ModuleProbe moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.versioning && <ModuleVersions moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.quote && <ModuleQuote moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.leadscore && <ModuleLeadScore moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.toproject && <ModuleToProject moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.promoengine && <RetailPromoSimulator moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.popricing && <RetailPOItems item={detail} onRefresh={refreshDetail} />}
        {cfg.cedis && <RetailCedisReception item={detail} onRefresh={refreshDetail} />}
        {cfg.podist && <RetailPODistribution item={detail} onRefresh={refreshDetail} />}
        {cfg.recordpdf && <FieldRecordPDF item={detail} kind={cfg.recordpdf} />}
        {cfg.fieldticket && <FieldTicketOps moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.workorder && <WorkshopWorkOrder item={detail} onRefresh={refreshDetail} />}
        {cfg.landing && <ModuleLanding moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.email && <ModuleEmail moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.flow && <ModuleAutomation moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {(cfg.youtube || cfg.media) && <ModuleMedia item={detail} />}
        {cfg.blog && (detail?.imagen || detail?.contenido || detail?.cuerpo) && (
          <div className="mt-4 rounded-lg border border-navy/10 overflow-hidden" data-testid="module-blog">
            {detail.imagen && <img src={detail.imagen} alt="" className="w-full max-h-72 object-cover" />}
            <div className="p-4 text-sm text-navy/75 whitespace-pre-wrap leading-relaxed">{detail.cuerpo || detail.contenido}</div>
          </div>
        )}
        {cfg.assetbank && detail?.preview_url && (
          <div className="mt-4 rounded-lg overflow-hidden border border-navy/10" data-testid="asset-preview">
            {/\.(mp4|webm|mov)(\?|$)/i.test(detail.preview_url)
              ? <video src={detail.preview_url} controls className="w-full max-h-80" />
              : <img src={detail.preview_url} alt={detail.nombre} className="w-full max-h-80 object-contain bg-navy/[0.02]" />}
          </div>
        )}
        {cfg.audience && <ReadTracker moduleKey={key} item={detail} />}
        {cfg.comments && <ModuleComments moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {cfg.log && cfg.fields.find((fd) => fd.key === "estado") && (
          <div className="mt-4 border-t border-navy/10 pt-4" data-testid="module-status-changer">
            <label className="text-xs text-navy/50 mb-1 block">Cambiar estado</label>
            <SelectField value={detail.estado} options={cfg.fields.find((fd) => fd.key === "estado").options} onChange={(v) => setStatus(detail, v)} />
          </div>
        )}
        {cfg.log && <ModuleLog item={detail} />}
        {detail.origin && (
          <div className="mt-3 flex items-center gap-2 text-xs text-navy/50 bg-violet/5 border border-violet/15 rounded-lg px-3 py-2" data-testid="module-origin-badge">
            <Sparkles className="w-3.5 h-3.5 text-violet shrink-0" />
            <span className="truncate">Creado desde <b className="text-navy/70">{detail.origin.label}</b> en otra suite</span>
          </div>
        )}
        {!cfg.personal && <ModuleLinks moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        {!cfg.personal && !cfg.viewer && !cfg.computedOnly && <CrossSuiteTool moduleKey={key} item={detail} onRefresh={refreshDetail} />}
        <div className="flex justify-between items-center mt-4">
          <div className="flex items-center gap-2">
            {!cfg.personal && <button onClick={() => setShare(true)} className="btn btn-ghost text-sm" data-testid="module-share-open"><Share2 className="w-4 h-4" /> Compartir</button>}
            {cfg.reportPdf && (
              <button data-testid="module-report-pdf" className="btn btn-outline text-sm"
                onClick={async () => {
                  try {
                    const r = await api.get(`/mod/${key}/items/${detail.id}/report.pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(r.data); const a = document.createElement("a");
                    a.href = url; a.download = `${(detail[first.key] || "reporte")}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                  } catch { toast.error("No se pudo generar el PDF"); }
                }}>
                <Download className="w-4 h-4" /> Descargar PDF
              </button>
            )}
          </div>
          <button onClick={() => del(detail.id)} className="text-navy/30 hover:text-destructive" data-testid="module-delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </Modal>}

      {share && detail && <Modal title="Compartir" onClose={() => setShare(false)} testid="module-share-dialog">
        <ModuleShare moduleKey={key} item={detail} onClose={() => setShare(false)} />
      </Modal>}
    </OSLayout>
  );
}

function ModuleMedia({ item }) {
  const raw = item?.youtube_url || item?.video_url || item?.enlace || item?.audio_url || "";
  if (!raw) return null;
  const yt = raw.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|live\/))([\w-]{11})/);
  const isAudio = /\.(mp3|wav|m4a|aac|ogg)$/i.test(raw) || (item?.audio_url && item.audio_url === raw);
  const isVideoFile = /\.(mp4|webm|mov)$/i.test(raw);
  return (
    <div className="mt-4 rounded-lg overflow-hidden border border-navy/10 bg-navy/[0.02]" data-testid="module-media">
      {yt ? (
        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
          <iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${yt[1]}`}
            title="OMNIA media" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      ) : isAudio ? (
        <audio controls src={raw} className="w-full" data-testid="module-media-audio" />
      ) : isVideoFile ? (
        <video controls src={raw} className="w-full max-h-[420px]" data-testid="module-media-video" />
      ) : (
        <a href={raw} target="_blank" rel="noreferrer" className="block p-4 text-sm text-violet hover:underline">Abrir contenido ↗</a>
      )}
    </div>
  );
}

function Modal({ title, onClose, children, testid, wide, xwide }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className={`card-surface w-full ${xwide ? "max-w-4xl" : wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose}><X className="w-5 h-5 text-navy/40" /></button></div>
        {children}
      </motion.div>
    </div>
  );
}
